'use client'

import type {
  FilterCondition,
  FilterObject,
} from '@moment/trading-types/filter-expression'
import type { FilterListsMap } from '@/hooks/use-get-filter-lists-map'

import { toError } from '@moment/observability/logger'
import {
  BondType,
  CouponType,
  MoodysRating,
  Sector,
  SPRating,
  TreasurySubtype,
} from '@moment/trading-types/reference-data'
import { useCallback, useMemo, useState } from 'react'

import { useAppDispatch } from '@/app/store'
import Button, { ButtonVariant } from '@/components/atomic/button'
import { ButtonSize } from '@/components/atomic/button/variants'
import { ButtonMultiSelect } from '@/components/atomic/button-multi-select'
import Icon, { IconName } from '@/components/atomic/icon'
import Input from '@/components/atomic/input'
import { MultiSelect } from '@/components/atomic/multi-select'
import { Select } from '@/components/atomic/select'
import Slider from '@/components/atomic/slider'
import { BodySubtext } from '@/components/atomic/text'
import ModelConstraintCard from '@/components/molecules/model-constraint-card'
import ModelSectionCard from '@/components/molecules/model-section-card'
import { ResetButton } from '@/components/molecules/target-band/styles'
import useTieredSelector from '@/hooks/store/useTieredSelector'
import { createLogger } from '@/lib/logger'
import { type ModelBuilder, setModelField } from '@/reducers/model-builder'
import { GicsSectorL4 } from '@/types/enums/gics-sector-l4'
import { IceSectorL4 } from '@/types/enums/ice-sector-l4'
import { RatingMode } from '@/types/enums/models'
import { UseOfProceeds } from '@/types/enums/use-of-proceeds'
import { COUNTRY_LABELS } from '@/utils/presentation/bonds'
import {
  convertToTitleCase,
  formatAssetClass,
  formatMoney,
  formatPercentage,
} from '@/utils/presentation/formatting'
import { getInstrumentDataValueTransformer } from '@/utils/presentation/instrument-data-transforms'
import { getUSARegionOptions } from '@/utils/usa-region-options'

import { AllowBlockRequireMap, AllowMap } from './allow-map'
import { DEFAULT_MODEL_BUILDER_VALUES } from './default-values'
import { fieldsToOmit } from './helpers'
import InstrumentBulkUpload from './instrument-bulk-upload'
import { MunicipalStatePreference } from './municipal-state-preference'
import {
  ModelConstraintsGrid,
  ModelConstraintWidth,
  ModelFullWidthInput,
  ModelRatingRangeGrid,
  ModelRatingRangeLeftInput,
  ModelRatingRangeRightInput,
} from './styles'
import TickerBulkUpload from './ticker-bulk-upload'

const LOGGER = createLogger('components/pages/model-builder/universe-tab')

export type UniverseKeys = Extract<
  keyof ModelBuilder,
  | 'couponRange'
  | 'ytwRange'
  | 'priceRange'
  | 'durationRange'
  | 'liquidityScoreRange'
  | 'minSpRange'
  | 'maxSpRange'
  | 'minLowestRatingRange'
  | 'maxLowestRatingRange'
  | 'minMoodysRange'
  | 'maxMoodysRange'
  | 'issuesSizeRange'
  | 'couponType'
  | 'treasurySubtype'
  | 'excludeSectors'
  | 'excludeIssuers'
  | 'excludeUseOfProceeds'
  | 'municipalTaxableFederal'
  | 'privatePlacement'
  | 'regulationS'
  | 'amtApplicable'
  | 'assetClasses'
  | 'municipalStatePreference'
  | 'municipalState'
  | 'belowDeMinimisThreshold'
  | 'includeTickers'
  | 'excludeTickers'
  | 'includeInstruments'
  | 'excludeMunicipalStates'
  | 'includeCurrencies'
  | 'excludeStructures'
  | 'perpetual'
  | 'sinkingFund'
  | 'callable'
  | 'maturityRange'
  | 'nextCallRange'
  | 'workoutRange'
  | 'includeFilterList'
  | 'excludeFilterList'
  | 'includeCountryDomicile'
  | 'excludeGicsSectorL4'
  | 'excludeIceSectorL4'
  | 'fdicInsured'
  | 'vrdoIndicator'
>

export enum UniverseApiField {
  Type = 'type',
  MunicipalState = 'municipal_state',
  MunicipalStatePreference = 'municipal_state_preference',
  SpRating = 'sp_rating',
  LowestRating = 'lowest_rating',
  MoodysRating = 'moodys_rating',
  Coupon = 'coupon',
  IssueSize = 'issue_size',
  LiquidityInstitutionalAggregate = 'liquidity_institutional_aggregate',
  CloseYtw = 'close_ytw',
  ClosePrice = 'close_price',
  Duration = 'duration',
  CouponType = 'coupon_type',
  TreasurySubtype = 'treasury_subtype',
  UseOfProceeds = 'use_of_proceeds',
  Sector = 'sector',
  Issuer = 'issuer',
  MunicipalTaxableFederal = 'municipal_taxable_federal',
  IsAmtApplicable = 'is_amt_applicable',
  PrivatePlacement = 'private_placement',
  RegS = 'reg_s',
  BelowDeMinimisThreshold = 'below_de_minimis_threshold',
  IncludeTickers = 'ticker',
  IncludeIsins = 'isin',
  IncludeCusips = 'cusip',
  MaturityInYears = 'maturity_in_years',
  Currency = 'currency',
  NextCallInYears = 'next_call_in_years',
  Perpetual = 'perpetual',
  SinkingFund = 'sinking_fund',
  Callable = 'callable',
  Bullet = 'bullet',
  WorkoutInYears = 'workout_in_years',
  FdicInsured = 'fdic_insured',
  VrdoIndicator = 'vrdo_indicator',

  // Nonexistent fields with custom behaviour
  IncludeInstruments = 'instrument',
  ExcludeStructures = 'exclude_structures',
  IncludeListId = 'include_list_id',
  ExcludeListId = 'exclude_list_id',
  IncludeListField = 'include_list_field',
  ExcludeListField = 'exclude_list_field',
  ExcludeTickers = 'exclude_ticker',

  // Fields that exist in the API but have custom behaviour
  CountryDomicile = 'country_domicile',
  GicsSectorL4 = 'gics_sector_4',
  IceSectorL4 = 'ice_sector_4',
}

const UNIVERSE_KEYS_TO_API_FIELD: Record<UniverseKeys, UniverseApiField> = {
  assetClasses: UniverseApiField.Type,
  municipalState: UniverseApiField.MunicipalState,
  municipalStatePreference: UniverseApiField.MunicipalStatePreference,
  minSpRange: UniverseApiField.SpRating,
  maxSpRange: UniverseApiField.SpRating,
  minLowestRatingRange: UniverseApiField.LowestRating,
  maxLowestRatingRange: UniverseApiField.LowestRating,
  minMoodysRange: UniverseApiField.MoodysRating,
  maxMoodysRange: UniverseApiField.MoodysRating,
  couponRange: UniverseApiField.Coupon,
  issuesSizeRange: UniverseApiField.IssueSize,
  liquidityScoreRange: UniverseApiField.LiquidityInstitutionalAggregate,
  ytwRange: UniverseApiField.CloseYtw,
  priceRange: UniverseApiField.ClosePrice,
  durationRange: UniverseApiField.Duration,
  couponType: UniverseApiField.CouponType,
  treasurySubtype: UniverseApiField.TreasurySubtype,
  excludeUseOfProceeds: UniverseApiField.UseOfProceeds,
  excludeSectors: UniverseApiField.Sector,
  excludeIssuers: UniverseApiField.Issuer,
  excludeMunicipalStates: UniverseApiField.MunicipalState,
  municipalTaxableFederal: UniverseApiField.MunicipalTaxableFederal,
  amtApplicable: UniverseApiField.IsAmtApplicable,
  privatePlacement: UniverseApiField.PrivatePlacement,
  regulationS: UniverseApiField.RegS,
  belowDeMinimisThreshold: UniverseApiField.BelowDeMinimisThreshold,
  includeTickers: UniverseApiField.IncludeTickers,
  excludeTickers: UniverseApiField.IncludeTickers,
  includeInstruments: UniverseApiField.IncludeInstruments,
  includeCurrencies: UniverseApiField.Currency,
  excludeStructures: UniverseApiField.ExcludeStructures,
  perpetual: UniverseApiField.Perpetual,
  sinkingFund: UniverseApiField.SinkingFund,
  callable: UniverseApiField.Callable,
  maturityRange: UniverseApiField.MaturityInYears,
  nextCallRange: UniverseApiField.NextCallInYears,
  workoutRange: UniverseApiField.WorkoutInYears,
  includeFilterList: UniverseApiField.IncludeListId,
  excludeFilterList: UniverseApiField.ExcludeListId,
  includeCountryDomicile: UniverseApiField.CountryDomicile,
  excludeGicsSectorL4: UniverseApiField.GicsSectorL4,
  excludeIceSectorL4: UniverseApiField.IceSectorL4,
  fdicInsured: UniverseApiField.FdicInsured,
  vrdoIndicator: UniverseApiField.VrdoIndicator,
} as const

// The inverse of UNIVERSE_KEYS_TO_API_FIELD
export const API_FIELD_TO_UNIVERSE_KEYS: Record<
  UniverseApiField,
  UniverseKeys
> = Object.entries(UNIVERSE_KEYS_TO_API_FIELD).reduce(
  (acc, [key, value]) => {
    acc[value] = key as UniverseKeys
    return acc
  },
  {} as Record<UniverseApiField, UniverseKeys>,
)

export type SliderKeys = Extract<
  UniverseKeys,
  | 'couponRange'
  | 'ytwRange'
  | 'priceRange'
  | 'durationRange'
  | 'liquidityScoreRange'
  | 'issuesSizeRange'
  | 'maturityRange'
  | 'nextCallRange'
  | 'workoutRange'
>

type SelectKeys = Extract<
  UniverseKeys,
  | 'couponType'
  | 'treasurySubtype'
  | 'useOfProceeds'
  | 'municipalTaxableFederal'
  | 'amtApplicable'
  | 'privatePlacement'
  | 'regulationS'
  | 'minSpRange'
  | 'maxSpRange'
  | 'minLowestRatingRange'
  | 'maxLowestRatingRange'
  | 'minMoodysRange'
  | 'maxMoodysRange'
  | 'excludeSectors'
  | 'excludeIssuers'
  | 'excludeUseOfProceeds'
  | 'includeTickers'
  | 'excludeTickers'
  | 'includeInstruments'
  | 'includeCurrencies'
  | 'belowDeMinimisThreshold'
  | 'includeFilterList'
  | 'excludeFilterList'
  | 'includeCountryDomicile'
  | 'excludeGicsSectorL4'
  | 'excludeIceSectorL4'
  | 'fdicInsured'
  | 'vrdoIndicator'
>

export const SLIDER_DEFAULTS: {
  [S in SliderKeys]: {
    defaultValue: number[]
    min: number
    max: number
    step: number
  }
} = {
  couponRange: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.couponRange as number[],
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    min: (DEFAULT_MODEL_BUILDER_VALUES.couponRange as number[])[0]!,
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    max: (DEFAULT_MODEL_BUILDER_VALUES.couponRange as number[])[1]!,
    step: 0.01,
  },
  ytwRange: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.ytwRange as number[],
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    min: (DEFAULT_MODEL_BUILDER_VALUES.ytwRange as number[])[0]!,
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    max: (DEFAULT_MODEL_BUILDER_VALUES.ytwRange as number[])[1]!,
    step: 0.01,
  },
  priceRange: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.priceRange as number[],
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    min: (DEFAULT_MODEL_BUILDER_VALUES.priceRange as number[])[0]!,
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    max: (DEFAULT_MODEL_BUILDER_VALUES.priceRange as number[])[1]!,
    step: 0.01,
  },
  liquidityScoreRange: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.liquidityScoreRange as number[],
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    min: (DEFAULT_MODEL_BUILDER_VALUES.liquidityScoreRange as number[])[0]!,
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    max: (DEFAULT_MODEL_BUILDER_VALUES.liquidityScoreRange as number[])[1]!,
    step: 1,
  },
  issuesSizeRange: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.issuesSizeRange as number[],
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    min: (DEFAULT_MODEL_BUILDER_VALUES.issuesSizeRange as number[])[0]!,
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    max: (DEFAULT_MODEL_BUILDER_VALUES.issuesSizeRange as number[])[1]!,
    step: 1_000_000,
  },
  durationRange: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.durationRange as number[],
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    min: (DEFAULT_MODEL_BUILDER_VALUES.durationRange as number[])[0]!,
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    max: (DEFAULT_MODEL_BUILDER_VALUES.durationRange as number[])[1]!,
    step: 0.01,
  },
  maturityRange: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.maturityRange as number[],
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    min: (DEFAULT_MODEL_BUILDER_VALUES.maturityRange as number[])[0]!,
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    max: (DEFAULT_MODEL_BUILDER_VALUES.maturityRange as number[])[1]!,
    step: 0.01,
  },
  nextCallRange: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.nextCallRange as number[],
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    min: (DEFAULT_MODEL_BUILDER_VALUES.nextCallRange as number[])[0]!,
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    max: (DEFAULT_MODEL_BUILDER_VALUES.nextCallRange as number[])[1]!,
    step: 0.01,
  },
  workoutRange: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.workoutRange as number[],
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    min: (DEFAULT_MODEL_BUILDER_VALUES.workoutRange as number[])[0]!,
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    max: (DEFAULT_MODEL_BUILDER_VALUES.workoutRange as number[])[1]!,
    step: 0.01,
  },
} as const

const SELECT_DEFAULTS: {
  [S in SelectKeys]: {
    defaultValue: string[] | string | null
  }
} = {
  couponType: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.couponType as CouponType[],
  },
  treasurySubtype: {
    defaultValue:
      DEFAULT_MODEL_BUILDER_VALUES.treasurySubtype as TreasurySubtype[],
  },
  excludeUseOfProceeds: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.excludeUseOfProceeds as string[],
  },
  municipalTaxableFederal: {
    defaultValue:
      DEFAULT_MODEL_BUILDER_VALUES.municipalTaxableFederal as string[],
  },
  belowDeMinimisThreshold: {
    defaultValue:
      DEFAULT_MODEL_BUILDER_VALUES.belowDeMinimisThreshold as string[],
  },
  amtApplicable: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.amtApplicable as string[],
  },
  privatePlacement: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.privatePlacement as string[],
  },
  regulationS: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.regulationS as string[],
  },
  minSpRange: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.minSpRange,
  },
  maxSpRange: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.maxSpRange,
  },
  minLowestRatingRange: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.minLowestRatingRange,
  },
  maxLowestRatingRange: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.maxLowestRatingRange,
  },
  minMoodysRange: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.minMoodysRange,
  },
  maxMoodysRange: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.maxMoodysRange,
  },
  excludeSectors: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.excludeSectors as string[],
  },
  excludeIssuers: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.excludeIssuers as string[],
  },
  includeTickers: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.includeTickers as string[],
  },
  excludeTickers: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.excludeTickers as string[],
  },
  includeInstruments: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.includeInstruments as string[],
  },
  includeCurrencies: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.includeCurrencies as string[],
  },
  includeFilterList: {
    defaultValue: null,
  },
  excludeFilterList: {
    defaultValue: null,
  },
  includeCountryDomicile: {
    defaultValue:
      DEFAULT_MODEL_BUILDER_VALUES.includeCountryDomicile as string[],
  },
  excludeGicsSectorL4: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.excludeGicsSectorL4 as string[],
  },
  excludeIceSectorL4: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.excludeIceSectorL4 as string[],
  },
  fdicInsured: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.fdicInsured as string[],
  },
  vrdoIndicator: {
    defaultValue: DEFAULT_MODEL_BUILDER_VALUES.vrdoIndicator as string[],
  },
} as const

interface UniverseTabProps {
  filterListsMap: FilterListsMap
  isFilterListsLoading: boolean
  filterListsError?: unknown
}

const fieldsToOmitForNonCorporates = [
  'excludeSectors',
  'includeCountryDomicile',
] as const
const fieldsToOmitForNonMunicipals = [
  'excludeUseOfProceeds',
  'municipalTaxableFederal',
  'amtApplicable',
  'belowDeMinimisThreshold',
  'vrdoIndicator',
] as const
const fieldsToOmitForNonTreasuries = ['treasurySubtype'] as const
const fieldsToOmitForNonCDs = ['fdicInsured'] as const

const fieldsToOmitGovernmentFields = [
  'privatePlacement',
  'regulationS',
  'minSpRange',
  'maxSpRange',
  'minLowestRatingRange',
  'maxLowestRatingRange',
  'minMoodysRange',
  'maxMoodysRange',
  'excludeStructures',
] as const

export const nonApplicableUniverseFields: Record<BondType, UniverseKeys[]> = {
  [BondType.Agency]: [
    ...fieldsToOmitForNonCorporates,
    ...fieldsToOmitForNonMunicipals,
    ...fieldsToOmitForNonTreasuries,
    ...fieldsToOmitForNonCDs,
    ...fieldsToOmitGovernmentFields,
  ],
  [BondType.CD]: [
    ...fieldsToOmitForNonCorporates,
    ...fieldsToOmitForNonMunicipals,
    ...fieldsToOmitForNonTreasuries,
  ],
  [BondType.Corporate]: [
    ...fieldsToOmitForNonMunicipals,
    ...fieldsToOmitForNonTreasuries,
    ...fieldsToOmitForNonCDs,
  ],
  [BondType.Government]: [
    ...fieldsToOmitForNonCorporates,
    ...fieldsToOmitForNonMunicipals,
    ...fieldsToOmitForNonTreasuries,
    ...fieldsToOmitForNonCDs,
  ],
  [BondType.MoneyMarket]: [
    ...fieldsToOmitForNonCorporates,
    ...fieldsToOmitForNonMunicipals,
    ...fieldsToOmitForNonTreasuries,
    ...fieldsToOmitForNonCDs,
  ],
  [BondType.Municipal]: [
    ...fieldsToOmitForNonCorporates,
    ...fieldsToOmitForNonTreasuries,
    ...fieldsToOmitForNonCDs,
  ],
  [BondType.Securitized]: [
    ...fieldsToOmitForNonCorporates,
    ...fieldsToOmitForNonMunicipals,
    ...fieldsToOmitForNonTreasuries,
    ...fieldsToOmitForNonCDs,
  ],
  [BondType.Treasury]: [
    ...fieldsToOmitForNonCorporates,
    ...fieldsToOmitForNonMunicipals,
    ...fieldsToOmitForNonCDs,
    ...fieldsToOmitGovernmentFields,
  ],
}

function UniverseTab({
  filterListsMap,
  isFilterListsLoading,
  filterListsError,
}: UniverseTabProps) {
  const dispatch = useAppDispatch()
  const model = useTieredSelector(
    (state) => state.modelBuilder?.model,
  ) as ModelBuilder

  const toOmit = useMemo(() => {
    const assetClasses = model?.assetClasses || []
    const municipalStatePreference = model?.municipalStatePreference

    // Use fieldsToOmit helper to get fields that should be omitted for all selected asset classes
    const omittedFields = [...fieldsToOmit(assetClasses)]

    // If the municipal state preference is not set, or is set to state focused,
    // then we need to omit the exclude municipal states field
    if (
      !municipalStatePreference ||
      municipalStatePreference === MunicipalStatePreference.StateFocused
    ) {
      omittedFields.push('excludeMunicipalStates')
    }

    return omittedFields
  }, [model?.assetClasses, model?.municipalStatePreference])

  const handleModelFieldChange = useCallback(
    <T extends ModelBuilder[keyof ModelBuilder]>(
      field: keyof typeof DEFAULT_MODEL_BUILDER_VALUES,
    ) => {
      return (value: T) => {
        dispatch(setModelField({ field, value }))
      }
    },
    [dispatch],
  )

  // Forces a Slider remount on reset without tying the key to the slider's
  // value (value-based keys cause mid-typing remounts when debounce round-trips).
  const [resetCounter, setResetCounter] = useState<
    Partial<Record<UniverseKeys, number>>
  >({})
  const sliderKey = (field: SliderKeys) => resetCounter[field] ?? 0

  const handleReset = useCallback(
    <T extends ModelBuilder[keyof ModelBuilder]>(
      field: UniverseKeys,
      defaultValue: T,
    ) => {
      return () => {
        setResetCounter((prev) => ({
          ...prev,
          [field]: (prev[field] ?? 0) + 1,
        }))
        dispatch(setModelField({ field, value: defaultValue }))
      }
    },
    [dispatch],
  )

  const handleResetSpRange = useCallback(() => {
    dispatch(
      setModelField({
        field: 'minSpRange',
        value: DEFAULT_MODEL_BUILDER_VALUES.minSpRange,
      }),
    )
    dispatch(
      setModelField({
        field: 'maxSpRange',
        value: DEFAULT_MODEL_BUILDER_VALUES.maxSpRange,
      }),
    )
    dispatch(
      setModelField({
        field: 'spRatingMode',
        value: DEFAULT_MODEL_BUILDER_VALUES.spRatingMode,
      }),
    )
  }, [dispatch])

  const handleResetLowestRatingRange = useCallback(() => {
    dispatch(
      setModelField({
        field: 'minLowestRatingRange',
        value: DEFAULT_MODEL_BUILDER_VALUES.minLowestRatingRange,
      }),
    )
    dispatch(
      setModelField({
        field: 'maxLowestRatingRange',
        value: DEFAULT_MODEL_BUILDER_VALUES.maxLowestRatingRange,
      }),
    )
    dispatch(
      setModelField({
        field: 'lowestRatingMode',
        value: DEFAULT_MODEL_BUILDER_VALUES.lowestRatingMode,
      }),
    )
  }, [dispatch])

  const handleResetMoodysRange = useCallback(() => {
    dispatch(
      setModelField({
        field: 'minMoodysRange',
        value: DEFAULT_MODEL_BUILDER_VALUES.minMoodysRange,
      }),
    )
    dispatch(
      setModelField({
        field: 'maxMoodysRange',
        value: DEFAULT_MODEL_BUILDER_VALUES.maxMoodysRange,
      }),
    )
    dispatch(
      setModelField({
        field: 'moodysRatingMode',
        value: DEFAULT_MODEL_BUILDER_VALUES.moodysRatingMode,
      }),
    )
  }, [dispatch])

  const formatPercentageValue = useCallback((value: number) => {
    return formatPercentage(value, { numDigits: 2 })
  }, [])

  const formatPriceValue = useCallback((value: number) => {
    return formatMoney(value, { numDigits: 0 })
  }, [])

  const formatIssueSizeValue = useCallback((value: number) => {
    // biome-ignore lint/style/useTemplate: Bulk suppression as part of biome migration
    return formatMoney(value / 1_000_000, { numDigits: 1 }) + 'MM'
  }, [])

  const resetButton = useCallback(
    <T extends ModelBuilder[keyof ModelBuilder]>(
      field: UniverseKeys,
      defaultValue: T,
    ) => <ResetButton onClick={handleReset(field, defaultValue)} />,
    [handleReset],
  )

  const allowOptions = useMemo(() => {
    return [
      { label: AllowMap.Allow.label, value: AllowMap.Allow.value },
      { label: AllowMap.DontAllow.label, value: AllowMap.DontAllow.value },
    ]
  }, [])

  const fdicInsuredOptions = useMemo(() => {
    return [
      { label: 'Enforced', value: AllowMap.Allow.value },
      { label: 'Not enforced', value: AllowMap.DontAllow.value },
    ]
  }, [])

  const allowBlockRequireOptions = useMemo(() => {
    return [
      {
        label: AllowBlockRequireMap.Allow.label,
        value: AllowBlockRequireMap.Allow.value,
      },
      {
        label: AllowBlockRequireMap.Block.label,
        value: AllowBlockRequireMap.Block.value,
      },
      {
        label: AllowBlockRequireMap.Require.label,
        value: AllowBlockRequireMap.Require.value,
      },
    ]
  }, [])

  const useOfProceedsOptions = useMemo(() => {
    return Object.values(UseOfProceeds).map((useOfProceeds) => ({
      label: useOfProceeds,
      value: useOfProceeds,
    }))
  }, [])

  const currencyOptions = useMemo(() => [{ label: 'USD', value: 'USD' }], [])

  const listOptions = useMemo(() => {
    if (!filterListsMap) return []
    return Object.values(filterListsMap).map((list) => ({
      label: `${list.name} (${list.field})`,
      value: JSON.stringify({ listId: list.list_id, listField: list.field }),
    }))
  }, [filterListsMap])

  const getCurrentListValue = useCallback(
    (filterList: ModelBuilder['includeFilterList']) => {
      if (!filterList?.list || !filterListsMap) return ''

      const matchingList = filterListsMap[filterList.list.list_id]
      if (
        !matchingList ||
        matchingList.field.toLowerCase() !== filterList.list.field.toLowerCase()
      ) {
        return ''
      }

      return JSON.stringify({
        listId: matchingList.list_id,
        listField: matchingList.field,
      })
    },
    [filterListsMap],
  )

  const handleListChange = useCallback(
    (field: 'includeFilterList' | 'excludeFilterList') => {
      return (value: string) => {
        if (!value) {
          dispatch(setModelField({ field, value: null }))
          return
        }

        try {
          const parsed = JSON.parse(value)
          const currentValue = model?.[field]

          dispatch(
            setModelField({
              field,
              value: {
                list: {
                  list_id: parsed.listId,
                  field: parsed.listField,
                  name: filterListsMap?.[parsed.listId]?.name || parsed.listId, // Use lookup map
                },
                scope: currentValue?.scope,
              },
            }),
          )
        } catch (error) {
          LOGGER.error('Failed to parse list value', { error: toError(error) })
        }
      }
    },
    [dispatch, filterListsMap, model],
  )

  const scopeOptions = useMemo(() => {
    const options = [{ label: 'All', value: 'all' }]
    if (model?.assetClasses && model.assetClasses.length > 0) {
      const assetClassOptions = model.assetClasses.map((assetClass) => ({
        label: formatAssetClass(assetClass),
        value: assetClass,
      }))
      options.push(...assetClassOptions)
    }
    return options
  }, [model?.assetClasses])

  const getCurrentScopeValue = useCallback(() => {
    if (!model?.includeFilterList?.scope) return 'all'
    const scope = model.includeFilterList.scope as FilterCondition
    // biome-ignore lint/complexity/useLiteralKeys: Bulk suppression as part of biome migration
    const scopeType = scope?.['type']
    if (scopeType && 'in' in scopeType && Array.isArray(scopeType.in)) {
      // If scope has a single asset class, return it; otherwise return 'all'
      if (scopeType.in.length === 1) {
        return scopeType.in[0]
      }
    }
    return 'all'
  }, [model?.includeFilterList?.scope])

  const handleScopeChange = useCallback(
    (value: string) => {
      const list = model?.includeFilterList?.list

      // Convert dropdown value to FilterObject
      const scope: FilterObject | undefined =
        value === 'all'
          ? undefined
          : ({
              type: {
                in: [value as BondType],
              },
            } as FilterObject)

      dispatch(
        setModelField({
          field: 'includeFilterList',
          value: {
            list,
            scope,
          } as ModelBuilder['includeFilterList'],
        }),
      )
    },
    [dispatch, model?.includeFilterList?.list],
  )

  return (
    <ModelSectionCard
      title="Universe constraints"
      description="Limit the bonds that are eligible in this model."
      icon={IconName.CircleStackIcon}
      iconColor="text-moment-green-800 dark:text-moment-green-600"
      iconBackgroundColor="bg-moment-green-100 dark:bg-moment-green-1000"
    >
      <ModelConstraintsGrid>
        {!toOmit.includes('couponRange') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="Coupon range"
              rightHeaderContent={resetButton(
                'couponRange',
                SLIDER_DEFAULTS.couponRange.defaultValue,
              )}
            >
              <Slider
                key={sliderKey('couponRange')}
                defaultValue={
                  model.couponRange ??
                  (SLIDER_DEFAULTS.couponRange.defaultValue as number[])
                }
                min={SLIDER_DEFAULTS.couponRange.min}
                max={SLIDER_DEFAULTS.couponRange.max}
                step={SLIDER_DEFAULTS.couponRange.step}
                formatter={formatPercentageValue}
                className="w-full"
                showMinMaxInputs
                onValueChange={handleModelFieldChange('couponRange')}
              />
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        {!toOmit.includes('ytwRange') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="Yield to worst"
              rightHeaderContent={resetButton(
                'ytwRange',
                SLIDER_DEFAULTS.ytwRange.defaultValue,
              )}
            >
              <Slider
                key={sliderKey('ytwRange')}
                defaultValue={
                  model.ytwRange ??
                  (SLIDER_DEFAULTS.ytwRange.defaultValue as number[])
                }
                // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
                min={SLIDER_DEFAULTS.ytwRange.min!}
                // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
                max={SLIDER_DEFAULTS.ytwRange.max!}
                step={SLIDER_DEFAULTS.ytwRange.step}
                className="w-full"
                showMinMaxInputs
                formatter={formatPercentageValue}
                onValueChange={handleModelFieldChange('ytwRange')}
              />
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        {!toOmit.includes('priceRange') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="Price range"
              rightHeaderContent={resetButton(
                'priceRange',
                SLIDER_DEFAULTS.priceRange.defaultValue,
              )}
            >
              <Slider
                key={sliderKey('priceRange')}
                defaultValue={
                  model.priceRange ??
                  (SLIDER_DEFAULTS.priceRange.defaultValue as number[])
                }
                // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
                min={SLIDER_DEFAULTS.priceRange.min!}
                // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
                max={SLIDER_DEFAULTS.priceRange.max!}
                step={SLIDER_DEFAULTS.priceRange.step}
                className="w-full"
                showMinMaxInputs
                formatter={formatPriceValue}
                onValueChange={handleModelFieldChange('priceRange')}
              />
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        {!toOmit.includes('maturityRange') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="Duration range"
              rightHeaderContent={resetButton(
                'durationRange',
                SLIDER_DEFAULTS.durationRange.defaultValue,
              )}
            >
              <Slider
                key={sliderKey('durationRange')}
                defaultValue={
                  model.durationRange ??
                  (SLIDER_DEFAULTS.durationRange.defaultValue as number[])
                }
                // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
                min={SLIDER_DEFAULTS.durationRange.min!}
                // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
                max={SLIDER_DEFAULTS.durationRange.max!}
                step={SLIDER_DEFAULTS.durationRange.step}
                className="w-full"
                showMinMaxInputs
                onValueChange={handleModelFieldChange('durationRange')}
              />
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        {!toOmit.includes('maturityRange') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="Maturity range"
              rightHeaderContent={resetButton(
                'maturityRange',
                SLIDER_DEFAULTS.maturityRange.defaultValue,
              )}
            >
              <Slider
                key={sliderKey('maturityRange')}
                defaultValue={
                  model.maturityRange ??
                  (SLIDER_DEFAULTS.maturityRange.defaultValue as number[])
                }
                // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
                min={SLIDER_DEFAULTS.maturityRange.min!}
                // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
                max={SLIDER_DEFAULTS.maturityRange.max!}
                step={SLIDER_DEFAULTS.maturityRange.step}
                className="w-full"
                showMinMaxInputs
                onValueChange={handleModelFieldChange('maturityRange')}
              />
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        {!toOmit.includes('nextCallRange') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="Next call range"
              rightHeaderContent={resetButton(
                'nextCallRange',
                SLIDER_DEFAULTS.nextCallRange.defaultValue,
              )}
            >
              <Slider
                key={sliderKey('nextCallRange')}
                defaultValue={
                  model.nextCallRange ??
                  (SLIDER_DEFAULTS.nextCallRange.defaultValue as number[])
                }
                // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
                min={SLIDER_DEFAULTS.nextCallRange.min!}
                // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
                max={SLIDER_DEFAULTS.nextCallRange.max!}
                step={SLIDER_DEFAULTS.nextCallRange.step}
                className="w-full"
                showMinMaxInputs
                formatter={(value: number) =>
                  `${value} yr${value !== 1 ? 's' : ''}`
                }
                onValueChange={handleModelFieldChange('nextCallRange')}
              />
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        {!toOmit.includes('workoutRange') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="Workout range"
              rightHeaderContent={resetButton(
                'workoutRange',
                SLIDER_DEFAULTS.workoutRange.defaultValue,
              )}
            >
              <Slider
                key={sliderKey('workoutRange')}
                defaultValue={
                  model.workoutRange ??
                  (SLIDER_DEFAULTS.workoutRange.defaultValue as number[])
                }
                // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
                min={SLIDER_DEFAULTS.workoutRange.min!}
                // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
                max={SLIDER_DEFAULTS.workoutRange.max!}
                step={SLIDER_DEFAULTS.workoutRange.step}
                className="w-full"
                showMinMaxInputs
                formatter={(value: number) =>
                  `${value} yr${value !== 1 ? 's' : ''}`
                }
                onValueChange={handleModelFieldChange('workoutRange')}
              />
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        {!toOmit.includes('liquidityScoreRange') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="Liquidity score"
              rightHeaderContent={resetButton(
                'liquidityScoreRange',
                SLIDER_DEFAULTS.liquidityScoreRange.defaultValue,
              )}
            >
              <Slider
                key={sliderKey('liquidityScoreRange')}
                defaultValue={
                  model.liquidityScoreRange ??
                  (SLIDER_DEFAULTS.liquidityScoreRange.defaultValue as number[])
                }
                // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
                min={SLIDER_DEFAULTS.liquidityScoreRange.min!}
                // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
                max={SLIDER_DEFAULTS.liquidityScoreRange.max!}
                step={SLIDER_DEFAULTS.liquidityScoreRange.step}
                className="w-full"
                showMinMaxInputs
                onValueChange={handleModelFieldChange('liquidityScoreRange')}
              />
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        {!toOmit.includes('minSpRange') && !toOmit.includes('maxSpRange') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="S&P rating range"
              rightHeaderContent={<ResetButton onClick={handleResetSpRange} />}
            >
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap gap-2">
                  <Button
                    label="Investment grade"
                    variant={
                      model.spRatingMode === 'investment'
                        ? ButtonVariant.Primary
                        : ButtonVariant.Secondary
                    }
                    size={ButtonSize.Small}
                    onClick={() => {
                      dispatch(
                        setModelField({
                          field: 'spRatingMode',
                          value: 'investment',
                        }),
                      )
                      dispatch(
                        setModelField({ field: 'minSpRange', value: 'BBB-' }),
                      )
                      dispatch(
                        setModelField({ field: 'maxSpRange', value: 'AAA' }),
                      )
                    }}
                  />
                  <Button
                    label="High yield"
                    variant={
                      model.spRatingMode === 'highYield'
                        ? ButtonVariant.Primary
                        : ButtonVariant.Secondary
                    }
                    size={ButtonSize.Small}
                    onClick={() => {
                      dispatch(
                        setModelField({
                          field: 'spRatingMode',
                          value: 'highYield',
                        }),
                      )
                      dispatch(
                        setModelField({ field: 'minSpRange', value: 'D' }),
                      )
                      dispatch(
                        setModelField({ field: 'maxSpRange', value: 'BB+' }),
                      )
                    }}
                  />
                  <Button
                    label="Custom range"
                    variant={
                      model.spRatingMode === RatingMode.Custom
                        ? ButtonVariant.Primary
                        : ButtonVariant.Secondary
                    }
                    size={ButtonSize.Small}
                    onClick={() => {
                      dispatch(
                        setModelField({
                          field: 'spRatingMode',
                          value: RatingMode.Custom,
                        }),
                      )
                    }}
                  />
                  <Button
                    label="None"
                    variant={
                      model.spRatingMode === RatingMode.None
                        ? ButtonVariant.Primary
                        : ButtonVariant.Secondary
                    }
                    size={ButtonSize.Small}
                    onClick={() => {
                      dispatch(
                        setModelField({
                          field: 'minSpRange',
                          value: null,
                        }),
                      )
                      dispatch(
                        setModelField({
                          field: 'maxSpRange',
                          value: null,
                        }),
                      )
                      dispatch(
                        setModelField({
                          field: 'spRatingMode',
                          value: RatingMode.None,
                        }),
                      )
                    }}
                  />
                </div>
                {model.spRatingMode === RatingMode.Custom && (
                  <ModelRatingRangeGrid>
                    <ModelRatingRangeLeftInput>
                      <Select
                        defaultValue={model.minSpRange}
                        key={`min-sp-range-select-${model.minSpRange}`}
                        slotProps={{
                          label: 'Min',
                          side: 'left',
                        }}
                        options={Object.values(SPRating).map((rating) => ({
                          label: rating,
                          value: rating,
                        }))}
                        onValueChange={handleModelFieldChange('minSpRange')}
                      />
                    </ModelRatingRangeLeftInput>
                    <ModelRatingRangeRightInput>
                      <Select
                        defaultValue={model.maxSpRange}
                        key={`max-sp-range-select-${model.maxSpRange}`}
                        slotProps={{
                          label: 'Max',
                          side: 'left',
                        }}
                        options={Object.values(SPRating).map((rating) => ({
                          label: rating,
                          value: rating,
                        }))}
                        onValueChange={handleModelFieldChange('maxSpRange')}
                      />
                    </ModelRatingRangeRightInput>
                  </ModelRatingRangeGrid>
                )}
                {model.spRatingMode === RatingMode.Investment && (
                  <BodySubtext>Investment grade: BBB- to AAA</BodySubtext>
                )}
                {model.spRatingMode === RatingMode.HighYield && (
                  <BodySubtext>High yield: D to BB+</BodySubtext>
                )}
              </div>
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        {!toOmit.includes('minLowestRatingRange') &&
          !toOmit.includes('maxLowestRatingRange') && (
            <ModelConstraintWidth>
              <ModelConstraintCard
                title="Lowest rating range"
                rightHeaderContent={
                  <ResetButton onClick={handleResetLowestRatingRange} />
                }
              >
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap gap-2">
                    <Button
                      label="Investment grade"
                      variant={
                        model.lowestRatingMode === RatingMode.Investment
                          ? ButtonVariant.Primary
                          : ButtonVariant.Secondary
                      }
                      size={ButtonSize.Small}
                      onClick={() => {
                        dispatch(
                          setModelField({
                            field: 'lowestRatingMode',
                            value: RatingMode.Investment,
                          }),
                        )
                        dispatch(
                          setModelField({
                            field: 'minLowestRatingRange',
                            value: 'BBB-',
                          }),
                        )
                        dispatch(
                          setModelField({
                            field: 'maxLowestRatingRange',
                            value: 'AAA',
                          }),
                        )
                      }}
                    />
                    <Button
                      label="High yield"
                      variant={
                        model.lowestRatingMode === RatingMode.HighYield
                          ? ButtonVariant.Primary
                          : ButtonVariant.Secondary
                      }
                      size={ButtonSize.Small}
                      onClick={() => {
                        dispatch(
                          setModelField({
                            field: 'lowestRatingMode',
                            value: RatingMode.HighYield,
                          }),
                        )
                        dispatch(
                          setModelField({
                            field: 'minLowestRatingRange',
                            value: 'D',
                          }),
                        )
                        dispatch(
                          setModelField({
                            field: 'maxLowestRatingRange',
                            value: 'BB+',
                          }),
                        )
                      }}
                    />
                    <Button
                      label="Custom range"
                      variant={
                        model.lowestRatingMode === RatingMode.Custom
                          ? ButtonVariant.Primary
                          : ButtonVariant.Secondary
                      }
                      size={ButtonSize.Small}
                      onClick={() => {
                        dispatch(
                          setModelField({
                            field: 'lowestRatingMode',
                            value: RatingMode.Custom,
                          }),
                        )
                      }}
                    />
                    <Button
                      label="None"
                      variant={
                        model.lowestRatingMode === RatingMode.None
                          ? ButtonVariant.Primary
                          : ButtonVariant.Secondary
                      }
                      size={ButtonSize.Small}
                      onClick={() => {
                        dispatch(
                          setModelField({
                            field: 'minLowestRatingRange',
                            value: null,
                          }),
                        )
                        dispatch(
                          setModelField({
                            field: 'maxLowestRatingRange',
                            value: null,
                          }),
                        )
                        dispatch(
                          setModelField({
                            field: 'lowestRatingMode',
                            value: RatingMode.None,
                          }),
                        )
                      }}
                    />
                  </div>
                  {model.lowestRatingMode === RatingMode.Custom && (
                    <ModelRatingRangeGrid>
                      <ModelRatingRangeLeftInput>
                        <Select
                          defaultValue={model.minLowestRatingRange}
                          key={`min-lowest-rating-range-select-${model.minLowestRatingRange}`}
                          slotProps={{
                            label: 'Min',
                            side: 'left',
                          }}
                          options={Object.values(SPRating).map((rating) => ({
                            label: rating,
                            value: rating,
                          }))}
                          onValueChange={handleModelFieldChange(
                            'minLowestRatingRange',
                          )}
                        />
                      </ModelRatingRangeLeftInput>
                      <ModelRatingRangeRightInput>
                        <Select
                          defaultValue={model.maxLowestRatingRange}
                          key={`max-lowest-rating-range-select-${model.maxLowestRatingRange}`}
                          slotProps={{
                            label: 'Max',
                            side: 'left',
                          }}
                          options={Object.values(SPRating).map((rating) => ({
                            label: rating,
                            value: rating,
                          }))}
                          onValueChange={handleModelFieldChange(
                            'maxLowestRatingRange',
                          )}
                        />
                      </ModelRatingRangeRightInput>
                    </ModelRatingRangeGrid>
                  )}
                  {model.lowestRatingMode === RatingMode.Investment && (
                    <BodySubtext>Investment grade: BBB- to AAA</BodySubtext>
                  )}
                  {model.lowestRatingMode === RatingMode.HighYield && (
                    <BodySubtext>High yield: D to BB+</BodySubtext>
                  )}
                </div>
              </ModelConstraintCard>
            </ModelConstraintWidth>
          )}

        {!toOmit.includes('minMoodysRange') &&
          !toOmit.includes('maxMoodysRange') && (
            <ModelConstraintWidth>
              <ModelConstraintCard
                title="Moody's rating range"
                rightHeaderContent={
                  <ResetButton onClick={handleResetMoodysRange} />
                }
              >
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap gap-2">
                    <Button
                      label="Investment grade"
                      variant={
                        model.moodysRatingMode === RatingMode.Investment
                          ? ButtonVariant.Primary
                          : ButtonVariant.Secondary
                      }
                      size={ButtonSize.Small}
                      onClick={() => {
                        dispatch(
                          setModelField({
                            field: 'moodysRatingMode',
                            value: RatingMode.Investment,
                          }),
                        )
                        dispatch(
                          setModelField({
                            field: 'minMoodysRange',
                            value: 'Baa3',
                          }),
                        )
                        dispatch(
                          setModelField({
                            field: 'maxMoodysRange',
                            value: 'Aaa',
                          }),
                        )
                      }}
                    />
                    <Button
                      label="High yield"
                      variant={
                        model.moodysRatingMode === RatingMode.HighYield
                          ? ButtonVariant.Primary
                          : ButtonVariant.Secondary
                      }
                      size={ButtonSize.Small}
                      onClick={() => {
                        dispatch(
                          setModelField({
                            field: 'moodysRatingMode',
                            value: RatingMode.HighYield,
                          }),
                        )
                        dispatch(
                          setModelField({
                            field: 'minMoodysRange',
                            value: 'C',
                          }),
                        )
                        dispatch(
                          setModelField({
                            field: 'maxMoodysRange',
                            value: 'Ba1',
                          }),
                        )
                      }}
                    />
                    <Button
                      label="Custom range"
                      variant={
                        model.moodysRatingMode === RatingMode.Custom
                          ? ButtonVariant.Primary
                          : ButtonVariant.Secondary
                      }
                      size={ButtonSize.Small}
                      onClick={() => {
                        dispatch(
                          setModelField({
                            field: 'moodysRatingMode',
                            value: RatingMode.Custom,
                          }),
                        )
                      }}
                    />
                    <Button
                      label="None"
                      variant={
                        model.moodysRatingMode === RatingMode.None
                          ? ButtonVariant.Primary
                          : ButtonVariant.Secondary
                      }
                      size={ButtonSize.Small}
                      onClick={() => {
                        dispatch(
                          setModelField({
                            field: 'minMoodysRange',
                            value: null,
                          }),
                        )
                        dispatch(
                          setModelField({
                            field: 'maxMoodysRange',
                            value: null,
                          }),
                        )
                        dispatch(
                          setModelField({
                            field: 'moodysRatingMode',
                            value: RatingMode.None,
                          }),
                        )
                      }}
                    />
                  </div>
                  {model.moodysRatingMode === RatingMode.Custom && (
                    <ModelRatingRangeGrid>
                      <ModelRatingRangeLeftInput>
                        <Select
                          defaultValue={model.minMoodysRange}
                          key={`min-moodys-range-select-${model.minMoodysRange}`}
                          slotProps={{
                            label: 'Min',
                            side: 'left',
                          }}
                          options={Object.values(MoodysRating).map(
                            (rating) => ({
                              label: rating,
                              value: rating,
                            }),
                          )}
                          onValueChange={handleModelFieldChange(
                            'minMoodysRange',
                          )}
                        />
                      </ModelRatingRangeLeftInput>
                      <ModelRatingRangeRightInput>
                        <Select
                          defaultValue={model.maxMoodysRange}
                          key={`max-moodys-range-select-${model.maxMoodysRange}`}
                          slotProps={{
                            label: 'Max',
                            side: 'left',
                          }}
                          options={Object.values(MoodysRating).map(
                            (rating) => ({
                              label: rating,
                              value: rating,
                            }),
                          )}
                          onValueChange={handleModelFieldChange(
                            'maxMoodysRange',
                          )}
                        />
                      </ModelRatingRangeRightInput>
                    </ModelRatingRangeGrid>
                  )}
                  {model.moodysRatingMode === RatingMode.Investment && (
                    <BodySubtext>Investment grade: Baa3 to Aaa</BodySubtext>
                  )}
                  {model.moodysRatingMode === RatingMode.HighYield && (
                    <BodySubtext>High yield: C to Ba1</BodySubtext>
                  )}
                </div>
              </ModelConstraintCard>
            </ModelConstraintWidth>
          )}

        {!toOmit.includes('issuesSizeRange') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="Issue size range"
              rightHeaderContent={resetButton(
                'issuesSizeRange',
                SLIDER_DEFAULTS.issuesSizeRange.defaultValue,
              )}
            >
              <Slider
                key={sliderKey('issuesSizeRange')}
                defaultValue={
                  model.issuesSizeRange ??
                  (SLIDER_DEFAULTS.issuesSizeRange.defaultValue as number[])
                }
                // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
                min={SLIDER_DEFAULTS.issuesSizeRange.min!}
                // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
                max={SLIDER_DEFAULTS.issuesSizeRange.max!}
                step={SLIDER_DEFAULTS.issuesSizeRange.step}
                showMinMaxInputs
                formatter={formatIssueSizeValue}
                onValueChange={handleModelFieldChange('issuesSizeRange')}
                large
              />
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        {!toOmit.includes('couponType') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="Coupon type"
              rightHeaderContent={resetButton(
                'couponType',
                SELECT_DEFAULTS.couponType.defaultValue,
              )}
            >
              <ButtonMultiSelect
                key={`coupon-type-button-multi-select-${model.couponType}`}
                columns={3}
                isMultiSelect
                defaultValue={
                  model.couponType ??
                  (SELECT_DEFAULTS.couponType.defaultValue as CouponType[])
                }
                options={Object.values(CouponType).map((type) => ({
                  label: getInstrumentDataValueTransformer('coupon_type')(
                    type as never,
                  ),
                  value: type,
                }))}
                onValueChange={handleModelFieldChange('couponType')}
              />
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        {!toOmit.includes('treasurySubtype') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="Treasury subtype"
              rightHeaderContent={resetButton(
                'treasurySubtype',
                SELECT_DEFAULTS.treasurySubtype.defaultValue,
              )}
            >
              <ButtonMultiSelect
                key={`treasury-subtype-button-multi-select-${model.treasurySubtype}`}
                columns={3}
                isMultiSelect
                defaultValue={
                  model.treasurySubtype ??
                  (SELECT_DEFAULTS.treasurySubtype
                    .defaultValue as TreasurySubtype[])
                }
                options={Object.values(TreasurySubtype).map((type) => ({
                  label: getInstrumentDataValueTransformer('treasury_subtype')(
                    type as never,
                  ),
                  value: type,
                }))}
                onValueChange={handleModelFieldChange('treasurySubtype')}
              />
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        {!toOmit.includes('excludeStructures') && (
          <ModelConstraintWidth>
            <ModelConstraintCard title="Exclude structures">
              <ModelFullWidthInput>
                <MultiSelect
                  value={
                    model.excludeStructures ??
                    DEFAULT_MODEL_BUILDER_VALUES.excludeStructures
                  }
                  options={[
                    { label: 'Perpetual', value: 'perpetual' },
                    { label: 'Sinking fund', value: 'sinking' },
                    { label: 'Callable', value: 'callable' },
                    { label: 'Bullet', value: 'bullet' },
                  ]}
                  onValueChange={handleModelFieldChange('excludeStructures')}
                  searchByLabel
                />
              </ModelFullWidthInput>
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        {!toOmit.includes('excludeMunicipalStates') && (
          <ModelConstraintWidth>
            <ModelConstraintCard title="Exclude municipal states">
              <ModelFullWidthInput>
                <MultiSelect
                  value={model.excludeMunicipalStates ?? []}
                  options={getUSARegionOptions}
                  onValueChange={(value: string[]) =>
                    handleModelFieldChange('excludeMunicipalStates')(value)
                  }
                  searchByLabel
                />
              </ModelFullWidthInput>
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        {!toOmit.includes('excludeSectors') && (
          <ModelConstraintWidth>
            <ModelConstraintCard title="Exclude sectors">
              <ModelFullWidthInput>
                <MultiSelect
                  value={model.excludeSectors ?? []}
                  options={Object.values(Sector).map((sector) => ({
                    label: convertToTitleCase(sector),
                    value: sector.toString(),
                  }))}
                  onValueChange={(value: string[]) =>
                    handleModelFieldChange('excludeSectors')(value)
                  }
                />
              </ModelFullWidthInput>
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        {!toOmit.includes('excludeUseOfProceeds') && (
          <ModelConstraintWidth>
            <ModelConstraintCard title="Exclude use of proceeds">
              <ModelFullWidthInput>
                <MultiSelect
                  value={
                    model.excludeUseOfProceeds ??
                    (SELECT_DEFAULTS.excludeUseOfProceeds
                      .defaultValue as string[])
                  }
                  options={useOfProceedsOptions}
                  onValueChange={handleModelFieldChange('excludeUseOfProceeds')}
                />
              </ModelFullWidthInput>
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        {!toOmit.includes('includeTickers') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="Include tickers"
              rightHeaderContent={<TickerBulkUpload field="includeTickers" />}
            >
              <ModelFullWidthInput>
                <Input
                  value={
                    Array.isArray(model.includeTickers)
                      ? model.includeTickers.join(', ')
                      : ''
                  }
                  placeholder="Enter tickers (comma separated)"
                  size={ButtonSize.Small}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const inputValue = e.target.value.trim()
                    if (!inputValue) {
                      handleModelFieldChange('includeTickers')([])
                    } else {
                      handleModelFieldChange('includeTickers')(
                        inputValue
                          .split(',')
                          .map((ticker) => ticker.trim())
                          .filter((ticker) => ticker.length > 0),
                      )
                    }
                  }}
                />
              </ModelFullWidthInput>
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        {!toOmit.includes('excludeTickers') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="Exclude tickers"
              rightHeaderContent={<TickerBulkUpload field="excludeTickers" />}
            >
              <ModelFullWidthInput>
                <Input
                  value={
                    Array.isArray(model.excludeTickers)
                      ? model.excludeTickers.join(', ')
                      : ''
                  }
                  placeholder="Enter tickers (comma separated)"
                  size={ButtonSize.Small}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const inputValue = e.target.value.trim()
                    if (!inputValue) {
                      handleModelFieldChange('excludeTickers')([])
                    } else {
                      handleModelFieldChange('excludeTickers')(
                        inputValue
                          .split(',')
                          .map((ticker) => ticker.trim())
                          .filter((ticker) => ticker.length > 0),
                      )
                    }
                  }}
                />
              </ModelFullWidthInput>
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        {!toOmit.includes('includeInstruments') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="Include Instruments (ISIN or CUSIP)"
              rightHeaderContent={<InstrumentBulkUpload />}
            >
              <ModelFullWidthInput>
                <div className="flex items-center gap-2">
                  <BodySubtext>
                    {model.includeInstruments?.length ?? 0} instruments selected
                  </BodySubtext>
                  {model.includeInstruments &&
                    model.includeInstruments.length > 0 && (
                      <Button
                        icon={<Icon iconName={IconName.TrashIcon} />}
                        variant={ButtonVariant.Ghost}
                        className="hover:text-moment-red-600"
                        onClick={() =>
                          handleModelFieldChange('includeInstruments')(
                            [] as string[],
                          )
                        }
                      />
                    )}
                </div>
              </ModelFullWidthInput>
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        <ModelConstraintWidth>
          <ModelConstraintCard
            title="Include Currencies"
            rightHeaderContent={resetButton(
              'includeCurrencies',
              DEFAULT_MODEL_BUILDER_VALUES.includeCurrencies,
            )}
          >
            <ModelFullWidthInput>
              <MultiSelect
                value={
                  model.includeCurrencies ??
                  DEFAULT_MODEL_BUILDER_VALUES.includeCurrencies ??
                  undefined
                }
                options={currencyOptions}
                onValueChange={handleModelFieldChange('includeCurrencies')}
              />
            </ModelFullWidthInput>
          </ModelConstraintCard>
        </ModelConstraintWidth>

        {!toOmit.includes('includeCountryDomicile') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="Include Country Domicile"
              rightHeaderContent={resetButton(
                'includeCountryDomicile',
                DEFAULT_MODEL_BUILDER_VALUES.includeCountryDomicile,
              )}
            >
              <ModelFullWidthInput>
                <MultiSelect
                  value={
                    model.includeCountryDomicile ??
                    DEFAULT_MODEL_BUILDER_VALUES.includeCountryDomicile ??
                    undefined
                  }
                  options={Object.entries(COUNTRY_LABELS).map(
                    ([code, displayName]) => ({
                      label: displayName,
                      value: code,
                    }),
                  )}
                  onValueChange={handleModelFieldChange(
                    'includeCountryDomicile',
                  )}
                  searchByLabel
                />
              </ModelFullWidthInput>
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        {!toOmit.includes('excludeGicsSectorL4') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="Exclude GICS Sector L4"
              rightHeaderContent={resetButton(
                'excludeGicsSectorL4',
                DEFAULT_MODEL_BUILDER_VALUES.excludeGicsSectorL4,
              )}
            >
              <ModelFullWidthInput>
                <MultiSelect
                  value={
                    model.excludeGicsSectorL4 ??
                    DEFAULT_MODEL_BUILDER_VALUES.excludeGicsSectorL4 ??
                    undefined
                  }
                  options={Object.values(GicsSectorL4).map((sector) => ({
                    label: sector,
                    value: sector,
                  }))}
                  onValueChange={handleModelFieldChange('excludeGicsSectorL4')}
                  searchByLabel
                />
              </ModelFullWidthInput>
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        {!toOmit.includes('excludeIceSectorL4') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="Exclude ICE Sector L4"
              rightHeaderContent={resetButton(
                'excludeIceSectorL4',
                DEFAULT_MODEL_BUILDER_VALUES.excludeIceSectorL4,
              )}
            >
              <ModelFullWidthInput>
                <MultiSelect
                  value={
                    model.excludeIceSectorL4 ??
                    DEFAULT_MODEL_BUILDER_VALUES.excludeIceSectorL4 ??
                    undefined
                  }
                  options={Object.values(IceSectorL4).map((sector) => ({
                    label: sector,
                    value: sector,
                  }))}
                  onValueChange={handleModelFieldChange('excludeIceSectorL4')}
                  searchByLabel
                />
              </ModelFullWidthInput>
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        <ModelConstraintWidth>
          <ModelConstraintCard
            title="Include Lists"
            rightHeaderContent={
              <div className="flex items-center gap-2">
                <Select
                  value={getCurrentScopeValue()}
                  options={scopeOptions}
                  onValueChange={handleScopeChange}
                  size="small"
                  disabled={!model?.includeFilterList?.list}
                />
                {resetButton(
                  'includeFilterList',
                  SELECT_DEFAULTS.includeFilterList.defaultValue,
                )}
              </div>
            }
          >
            <ModelFullWidthInput>
              <Select
                value={getCurrentListValue(model.includeFilterList)}
                options={listOptions}
                placeholder={
                  isFilterListsLoading
                    ? 'Loading lists...'
                    : // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
                      filterListsError
                      ? 'Error loading lists'
                      : 'Select lists to include'
                }
                disabled={isFilterListsLoading || !!filterListsError}
                onValueChange={handleListChange('includeFilterList')}
              />
            </ModelFullWidthInput>
          </ModelConstraintCard>
        </ModelConstraintWidth>

        <ModelConstraintWidth>
          <ModelConstraintCard
            title="Exclude Lists"
            rightHeaderContent={resetButton(
              'excludeFilterList',
              SELECT_DEFAULTS.excludeFilterList.defaultValue,
            )}
          >
            <ModelFullWidthInput>
              <Select
                value={getCurrentListValue(model.excludeFilterList)}
                options={listOptions}
                placeholder={
                  isFilterListsLoading
                    ? 'Loading lists...'
                    : // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
                      filterListsError
                      ? 'Error loading lists'
                      : 'Select lists to exclude'
                }
                disabled={isFilterListsLoading || !!filterListsError}
                onValueChange={handleListChange('excludeFilterList')}
              />
            </ModelFullWidthInput>
          </ModelConstraintCard>
        </ModelConstraintWidth>

        {!toOmit.includes('municipalTaxableFederal') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="Taxable munis"
              rightHeaderContent={resetButton(
                'municipalTaxableFederal',
                SELECT_DEFAULTS.municipalTaxableFederal.defaultValue,
              )}
            >
              <ButtonMultiSelect
                key={`municipal-taxable-federal-button-multi-select-${model.municipalTaxableFederal}`}
                columns={2}
                isMultiSelect={false}
                defaultValue={
                  model.municipalTaxableFederal ??
                  (SELECT_DEFAULTS.municipalTaxableFederal
                    .defaultValue as string[])
                }
                options={allowOptions}
                minSelected={1}
                onValueChange={handleModelFieldChange(
                  'municipalTaxableFederal',
                )}
              />
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        {!toOmit.includes('belowDeMinimisThreshold') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="Below de minimis threshold"
              rightHeaderContent={resetButton(
                'belowDeMinimisThreshold',
                SELECT_DEFAULTS.belowDeMinimisThreshold.defaultValue,
              )}
            >
              <ButtonMultiSelect
                key={`below-de-minimis-threshold-button-multi-select-${model.belowDeMinimisThreshold}`}
                columns={2}
                isMultiSelect={false}
                defaultValue={
                  model.belowDeMinimisThreshold ??
                  (SELECT_DEFAULTS.belowDeMinimisThreshold
                    .defaultValue as string[])
                }
                options={allowOptions}
                minSelected={1}
                onValueChange={handleModelFieldChange(
                  'belowDeMinimisThreshold',
                )}
              />
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        {!toOmit.includes('amtApplicable') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="AMT munis"
              rightHeaderContent={resetButton(
                'amtApplicable',
                SELECT_DEFAULTS.amtApplicable.defaultValue,
              )}
            >
              <ButtonMultiSelect
                key={`amt-button-multi-select-${model.amtApplicable}`}
                columns={2}
                isMultiSelect={false}
                defaultValue={
                  model.amtApplicable ??
                  (SELECT_DEFAULTS.amtApplicable.defaultValue as string[])
                }
                options={allowOptions}
                minSelected={1}
                onValueChange={handleModelFieldChange('amtApplicable')}
              />
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        {!toOmit.includes('privatePlacement') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="Private placement"
              rightHeaderContent={resetButton(
                'privatePlacement',
                SELECT_DEFAULTS.privatePlacement.defaultValue,
              )}
            >
              <ButtonMultiSelect
                key={`private-placements-button-multi-select-${model.privatePlacement}`}
                columns={2}
                isMultiSelect={false}
                defaultValue={
                  model.privatePlacement ??
                  (SELECT_DEFAULTS.privatePlacement.defaultValue as string[])
                }
                options={allowOptions}
                minSelected={1}
                onValueChange={handleModelFieldChange('privatePlacement')}
              />
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}

        {!toOmit.includes('regulationS') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="Regulation S"
              rightHeaderContent={resetButton(
                'regulationS',
                SELECT_DEFAULTS.regulationS.defaultValue,
              )}
            >
              <ButtonMultiSelect
                key={`regulation-s-button-multi-select-${model.regulationS}`}
                columns={2}
                isMultiSelect={false}
                defaultValue={
                  model.regulationS ??
                  (SELECT_DEFAULTS.regulationS.defaultValue as string[])
                }
                options={allowOptions}
                minSelected={1}
                onValueChange={handleModelFieldChange('regulationS')}
              />
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}
        {!toOmit.includes('fdicInsured') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="FDIC insured"
              rightHeaderContent={resetButton(
                'fdicInsured',
                SELECT_DEFAULTS.fdicInsured.defaultValue,
              )}
            >
              <ButtonMultiSelect
                key={`fdic-insured-button-multi-select-${model.fdicInsured}`}
                columns={2}
                isMultiSelect={false}
                defaultValue={
                  model.fdicInsured ??
                  (SELECT_DEFAULTS.fdicInsured.defaultValue as string[])
                }
                options={fdicInsuredOptions}
                minSelected={1}
                onValueChange={handleModelFieldChange('fdicInsured')}
              />
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}
        {!toOmit.includes('vrdoIndicator') && (
          <ModelConstraintWidth>
            <ModelConstraintCard
              title="VRDO"
              rightHeaderContent={resetButton(
                'vrdoIndicator',
                SELECT_DEFAULTS.vrdoIndicator.defaultValue,
              )}
            >
              <ButtonMultiSelect
                key={`vrdo-indicator-button-multi-select-${model.vrdoIndicator}`}
                columns={3}
                isMultiSelect={false}
                defaultValue={
                  model.vrdoIndicator ??
                  (SELECT_DEFAULTS.vrdoIndicator.defaultValue as string[])
                }
                options={allowBlockRequireOptions}
                minSelected={1}
                onValueChange={handleModelFieldChange('vrdoIndicator')}
              />
            </ModelConstraintCard>
          </ModelConstraintWidth>
        )}
      </ModelConstraintsGrid>
    </ModelSectionCard>
  )
}

// Dead UI retained for live constants in this file; unexported for knip.
void UniverseTab
